import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Users, Target, Eye, BookOpen, Church } from 'lucide-react';
import { aboutInfo, leadership, churchInfo } from '../mockData';

export const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#800020] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">About Us</h1>
          <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6"></div>
          <p className="text-xl text-[#fef3c7]">
            Discover our story, our mission, and the faith that guides us
          </p>
        </div>
      </section>

      {/* Church History */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#800020] mb-4">Our Story</h2>
              <div className="w-24 h-1 bg-[#D4AF37] mb-6"></div>
              <p className="text-gray-700 leading-relaxed mb-4">
                {aboutInfo.history}
              </p>
              <p className="text-gray-700 leading-relaxed">
                Today, under the leadership of {churchInfo.pastor.name}, we continue to be a beacon of hope and a house of prayer for all people. Our doors are open, our hearts are ready, and we invite you to become part of our church family.
              </p>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1580243152089-98da42694285?w=800" 
                alt="Church Community"
                className="rounded-lg shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-white border-[#800020] border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-[#800020] mb-4">Our Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#800020] border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-[#fffbeb] rounded-full flex items-center justify-center mb-6">
                  <Eye className="h-8 w-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-[#800020] mb-4">Our Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  {aboutInfo.vision}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Beliefs */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">What We Believe</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Our faith is rooted in the timeless truths of Scripture and the historic Baptist tradition
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {aboutInfo.coreBeliefs.map((belief, index) => (
              <Card key={index} className="bg-white border-[#800020] border hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3 mb-3">
                    <BookOpen className="h-6 w-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <h3 className="text-xl font-bold text-[#800020]">{belief.title}</h3>
                  </div>
                  <p className="text-gray-700">{belief.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 bg-[#fffbeb]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#800020] mb-4">Our Leadership</h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-4"></div>
            <p className="text-lg text-gray-700">
              Meet the dedicated servants leading our church family
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((leader, index) => (
              <Card key={index} className="bg-white hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <img 
                    src={leader.image} 
                    alt={leader.name}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-[#D4AF37]"
                    style={{
                      objectPosition: leader.name === "Freddie Wilson" ? "center 15%" : 
                                     leader.name === "Rev. Dr. H.C. Wilson" ? "center 25%" : 
                                     "center center"
                    }}
                  />
                  <h3 className="text-xl font-bold text-[#800020] mb-1">{leader.name}</h3>
                  <p className="text-[#D4AF37] font-semibold mb-3">{leader.title}</p>
                  <p className="text-gray-600 text-sm">{leader.bio}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Baptist Heritage */}
      <section className="py-16 bg-[#800020] text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center">
              <div className="w-48 h-48 bg-[#D4AF37] rounded-full flex items-center justify-center">
                <Church className="h-24 w-24 text-white" />
              </div>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-4">Our Baptist Heritage</h2>
              <div className="w-24 h-1 bg-[#D4AF37] mb-6"></div>
              <p className="text-[#fef3c7] leading-relaxed mb-4">
                As a Baptist church, we stand on the historic principles that have guided our denomination for centuries. We believe in the priesthood of all believers, the autonomy of the local church, and the authority of Scripture.
              </p>
              <p className="text-[#fef3c7] leading-relaxed">
                Our Baptist identity means we value religious liberty, believers' baptism, congregational governance, and the separation of church and state. These principles guide how we worship, organize, and serve.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
